// store/listings.js
import { defineStore } from 'pinia';
import sanctum from '@api/auth/sanctum';
import userAPI from '@api/user';
import meAPI from '@api/me';
import navigation from '@router/navigation';
import { toast } from 'vue-sonner';
import * as Sentry from '@sentry/vue';
import { isNoiseAxiosError } from '@api/axiosNoise';
import { buildCompanySettingsScopeKey } from '@/utilities/companySettingsScope';
import { useNotificationsStore } from './notifications';
import { useJobCountsStore } from './jobCounts';
import { useInboxCountsStore } from './inboxCounts';
import { useBulkImportStore } from './bulkImport';
import { resubscribeStaffAssignmentRealtime } from '@composables/useStaffAssignmentRealtime';
import { GENERIC_ERROR_MESSAGE } from '@api/errorHandler';

/**
 * The error a sign-in path shows when the API accepted the credentials but the
 * operator couldn't be loaded (CU-86d4ahttx). It keeps the processed-error
 * shape the sign-in views already read. The reason is the API's processed
 * message; anything thrown in the client, such as a TypeError, gets generic
 * copy instead of its own text.
 *
 * The message is repeated as an `account` error because ErrorDetails, which
 * every sign-in page uses, renders nothing for an error without field errors.
 */
function signInLoadError(error) {
    const apiMessage = !(error instanceof Error) && typeof error?.message === 'string' ? error.message.trim() : '';
    const reason = (apiMessage || GENERIC_ERROR_MESSAGE).replace(/\.$/, '');
    const message = `Signed in, but your account couldn't be loaded: ${reason}. Please sign in again.`;

    return {
        code: error?.code ?? null,
        message,
        errors: { account: [message] },
        links: null,
        data: null,
    };
}

/**
 * Helper to get current user roles.
 * Checks state.data.roles first, then falls back to current tenant's roles.
 * This handles the race condition where roles may not be set yet during initial load.
 */
function getCurrentRoles(state) {
    // If roles are already set on data, use them
    if (state.data?.roles?.length) {
        return state.data.roles;
    }
    // Fallback: get roles from current tenant
    if (state.tenantId && state.data?.tenants) {
        const currentTenant = state.data.tenants.find((t) => t.id === parseInt(state.tenantId));
        return currentTenant?.roles || [];
    }
    return [];
}

export const useUserStore = defineStore('user', {
    state: () => {
        return {
            loading: false,
            data: null,
            authenticated: false,
            // In-flight fetchUser() promise — awaited by the router guard so
            // role-gated navigations never rule on an unhydrated store (CU-86d3hdwu4).
            hydrationPromise: null,
            // The bearer the loaded user was hydrated with. A dropped reload keeps
            // the session only while it is still the current bearer (CU-86d4a566a).
            hydratedBearer: null,
            search: null,
            navigation: [],
            bearer: useStorage('bearer', null),
            tenantId: useStorage('tenantId', null),
            tenantSlug: useStorage('tenantSlug', null),
            tenantModalStatus: false,
            isMoveproAdmin: false,
            tenantUserSelected: useStorage('tenantUserSelected', false),
            activity_status: useStorage('activity_status', null),
            // MFA state
            mfaChallengeToken: null,
            mfaMethod: null,
            mfaSetupRequired: false,
            deviceTrustToken: useStorage('device_trust_token', null),
            deviceId: useStorage('device_id', null),
        };
    },
    actions: {
        ensureDeviceId() {
            if (!this.deviceId) {
                try {
                    this.deviceId = crypto.randomUUID();
                } catch {
                    // Fallback for insecure contexts (HTTP) where crypto.randomUUID() is unavailable
                    this.deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                        const r = (Math.random() * 16) | 0;
                        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
                    });
                }
            }
        },
        /**
         * Drop the stored token before a sign-in request. The axios interceptor
         * attaches whatever is stored, and LoginController revokes every token
         * of whoever a still-valid bearer belongs to — possibly another user who
         * last signed in on this browser (CU-86d4ahttx). The interceptor reads
         * localStorage directly, so clear it too: assigning null to a ref that
         * is already null writes nothing, and the stored value can drift from
         * the ref (an edit made in the same tab isn't synced back).
         */
        forgetStoredSession() {
            this.bearer = null;
            this.tenantSlug = null;
            localStorage.removeItem('bearer');
            localStorage.removeItem('tenantSlug');
        },

        async login(username, password, remember = false) {
            try {
                this.loading = true;
                this.ensureDeviceId();
                this.forgetStoredSession();

                this.tenantId = null;
                this.tenantUserSelected = false;
                this.mfaChallengeToken = null;
                this.mfaMethod = null;
                this.mfaSetupRequired = false;

                const response = await sanctum.login({
                    email: username,
                    password,
                    remember,
                    device_trust_token: this.deviceTrustToken,
                });

                // MFA challenge required (TOTP or OTP)
                if (response.two_factor) {
                    this.mfaChallengeToken = response.challenge_token;
                    this.mfaMethod = response.method;
                    this.loading = false;
                    return { mfaRequired: true, method: response.method };
                }

                // MFA setup required (user must configure TOTP before proceeding)
                if (response.mfa_setup_required) {
                    this.bearer = response.token;
                    this.mfaSetupRequired = true;
                    this.mfaMethod = response.method;
                    this.loading = false;
                    return { mfaSetupRequired: true, method: response.method };
                }

                // Normal login — no MFA
                this.bearer = response.token;

                if (response.device_trust_token) {
                    this.deviceTrustToken = response.device_trust_token;
                }

                const user = await this.fetchUser({ signIn: true });

                this.loading = false;

                return user;
            } catch (error) {
                this.authenticated = false;
                this.data = null;
                this.loading = false;

                // let the form component display the error
                throw error;
            }
        },

        async completeMfaChallenge(token, deviceTrustToken) {
            this.bearer = token;
            this.mfaChallengeToken = null;
            this.mfaMethod = null;

            if (deviceTrustToken) {
                this.deviceTrustToken = deviceTrustToken;
            }

            const user = await this.fetchUser({ signIn: true });
            return user;
        },

        async completeMfaSetup() {
            this.mfaSetupRequired = false;
            this.mfaMethod = null;

            const user = await this.fetchUser({ signIn: true });
            return user;
        },

        async retrieve(userId) {
            return await userAPI.retrieve(userId);
        },

        /**
         * Load the signed-in operator from /me.
         *
         * Takes `{ fetchNotifications, signIn }`, or the older positional
         * `fetchNotifications` boolean. `signIn` is for the sign-in paths only:
         * the load straight after a new token is issued (CU-86d4ahttx).
         */
        async fetchUser(options = {}) {
            const promise = this.runFetchUser(options);

            this.hydrationPromise = promise;

            try {
                return await promise;
            } finally {
                this.hydrationPromise = null;
            }
        },

        /**
         * GET /me, retried once on a 401 straight after sign-in. A session
         * cookie left from before a password change makes the API reject the
         * first request even with the new token, and that 401 drops the stale
         * session, so the retry goes through (CU-86d4ahttx). Nothing else is
         * retried: another status, a second 401, or any load outside sign-in.
         */
        async loadMe(signIn) {
            try {
                return await sanctum.me();
            } catch (error) {
                if (!signIn || error?.code !== 401) {
                    throw error;
                }

                return await sanctum.me();
            }
        },

        /**
         * Inner body of fetchUser(). Call fetchUser() instead — it registers the
         * in-flight hydration promise that the router guard awaits before
         * evaluating role-gated routes (CU-86d3hdwu4).
         */
        async runFetchUser(options = {}) {
            const { fetchNotifications = true, signIn = false } =
                typeof options === 'boolean' ? { fetchNotifications: options } : options;

            try {
                this.loading = true;

                const { data } = await this.loadMe(signIn);

                this.data = data;

                if (data.tenants.length > 0) {
                    const isMultiTenantFreshLogin = data.tenants.length > 1 && !this.tenantUserSelected;
                    const shouldFetchSideEffects = fetchNotifications && !isMultiTenantFreshLogin;

                    // if a tenant has already been set, use it if it exists for this user
                    if (this.tenantId) {
                        const validatedTenant = this.data.tenants.find(
                            (tenant) => tenant.id === parseInt(this.tenantId),
                        );

                        if (validatedTenant) {
                            await this.setTenant(validatedTenant.id, shouldFetchSideEffects);
                        } else {
                            await this.setTenant(data.tenants[0].id, shouldFetchSideEffects);
                        }
                    } else {
                        // otherwise, use the first tenant available for this user as the default
                        await this.setTenant(data.tenants[0].id, shouldFetchSideEffects);
                    }
                }

                this.activity_status = data.activity_status;
                this.authenticated = true;
                this.hydratedBearer = this.bearer;
                this.loading = false;

                return data;
            } catch (error) {
                // A dropped connection, a cancel or a browser abort is transport
                // noise, the same set processError keeps out of Sentry for every
                // other request. It is not reported here either, and it carries no
                // evidence the session ended: mid-session (a background reload from
                // UserActivityMonitor) the loaded user is kept and the operator stays
                // put. With nothing loaded yet, or once a new bearer has been
                // installed (accepting an invite), the load still fails, so one
                // user's data never sits beside another's token (CU-86d4a566a).
                const isNoise = isNoiseAxiosError(error);

                if (isNoise && this.authenticated && this.bearer === this.hydratedBearer) {
                    this.loading = false;

                    return;
                }

                // Report anything else before swallowing. This catch turns any other
                // throw in the hydration chain into a silent bounce back to /login
                // with no message on screen — a client-side TypeError in here looked
                // exactly like a healthy API and a healthy account. A timeout is
                // reported too: it is a real availability signal (CU-86d477a1g).
                if (!isNoise) {
                    Sentry.captureException(error, {
                        tags: { hydration_failure: 'fetchUser' },
                        extra: { route: this.router?.currentRoute?.value?.name ?? null },
                    });
                }

                this.authenticated = false;
                this.data = null;
                this.loading = false;

                // A sign-in that can't load the operator stays on its page and
                // says why, rather than bouncing back to an empty sign-in form.
                // The token the failed load used isn't kept, except when the API
                // asks for MFA setup: the axios interceptor is already opening
                // MfaSetup, which needs the token, as login() keeps it for the
                // same answer. Throwing still stops the page's own navigation.
                if (signIn) {
                    if (error?.code === 403 && error?.data?.mfa_setup_required) {
                        this.mfaSetupRequired = true;
                        this.mfaMethod = error.data.method ?? null;

                        throw error;
                    }

                    this.forgetStoredSession();

                    throw signInLoadError(error);
                }

                if (this.router && this.router.currentRoute.value.name !== 'Register') {
                    this.router.push('login');
                }
            }
        },

        /**
         * Resolve roles before a role-gated navigation (CU-86d3hdwu4).
         *
         * Roles can be momentarily unknowable — initial hydration still in
         * flight, or `data` replaced by a payload without roles/tenants. That is
         * different from a hydrated user who genuinely has no roles: only wait /
         * refetch in the unknowable case, then let the guard rule on real data.
         */
        async ensureRolesResolved() {
            if (this.currentRoles.length) {
                return true;
            }

            if (this.hydrationPromise) {
                // A hydration is already in flight — its result IS fresh server
                // truth, so rule on it without a second request.
                let data;

                try {
                    data = await this.hydrationPromise;
                } catch {
                    // runFetchUser handles its own failures
                }

                return this.rolesResolution(data);
            }

            // Empty roles at navigation time: always confirm with the server
            // once before letting the guard deny. This heals every clobbered /
            // stale client state; a genuinely role-less user is still denied
            // after the re-fetch (costing one extra /me per gated navigation).
            let data;

            try {
                data = await this.fetchUser(false);
            } catch {
                // runFetchUser handles its own failures
            }

            return this.rolesResolution(data);
        },

        /**
         * true when roles are known, false when the server confirmed there are
         * none, and null when the reload dropped and the session was kept: the
         * roles were never received, so the guard must not rule on them
         * (CU-86d4a566a).
         */
        rolesResolution(data) {
            if (this.currentRoles.length > 0) {
                return true;
            }

            return data === undefined && this.authenticated ? null : false;
        },

        async logout(silent = false) {
            try {
                this.loading = true;

                await sanctum.logout(silent);

                if (import.meta.env.VITE_ENV == 'production') {
                    Sentry.setUser(null);
                }

                this.data = null;
                this.authenticated = false;
                this.jwt = null;
                this.loading = false;

                this.tenantId = null;
                this.tenantSlug = null;
                this.tenantUserSelected = false;
            } catch (error) {
                this.data = null;
                this.authenticated = false;
                this.loading = false;

                throw error;
            } finally {
                this.loading = false;

                // Logging out is a route change, not a reload, so nothing else
                // disposes a store's timers. A bulk import left running would
                // go on polling a now-401 endpoint every two seconds for as
                // long as the tab stayed open. In the finally because the
                // operator is logged out locally either way — the catch above
                // clears `authenticated` even when the request fails.
                useBulkImportStore().teardown();
            }
        },

        async update(userData) {
            try {
                const { data } = await userAPI.update(this.data.id, userData);
                this.data = data.user;

                toast.success('User updated successfully');

                return data.user;
            } catch (error) {
                toast.error('User update failed');

                throw error;
            }
        },

        async requestResetPassword(email) {
            const { data } = await userAPI.requestResetPassword(email);

            if (data) {
                return true;
            }
        },

        async confirmResetPassword(token, email, password, password_confirmation) {
            const { data } = await userAPI.confirmResetPassword(token, email, password, password_confirmation);

            if (data) {
                return true;
            }
        },

        async setTenant(tenantId, fetchNotifications = true) {
            const tenant = this.data.tenants.find((tenant) => tenant.id === tenantId);

            if (tenant) {
                try {
                    await meAPI.setActiveTenant(tenantId);
                } catch (error) {
                    console.error('Failed to set active tenant in session:', error);
                    throw error;
                }

                this.tenantId = tenant.id;
                this.tenantSlug = tenant.slug;
                this.isMoveproAdmin = this.tenantId == 1000;

                // Set user's roles from the selected tenant's roles
                // This ensures all role checks use the correct per-tenant roles
                this.data.roles = tenant.roles || [];
            }

            if (import.meta.env.VITE_ENV == 'production') {
                Sentry.setUser({
                    id: this.data.id,
                    name: this.data.fullname,
                    email: this.data.email,
                    tenant_id: this.tenantId,
                    tenant_slug: this.tenantSlug,
                });
            }

            const companyStore = useCompanyStore();
            const stripePaymentStore = useStripePaymentStore();
            const defaultCompanyId = tenant?.default_company_id ?? null;
            const activeSettingsScopeKey = buildCompanySettingsScopeKey(tenant?.id, tenant?.slug, defaultCompanyId);

            if (companyStore.settingsScopeKey !== activeSettingsScopeKey) {
                companyStore.$reset();
            }

            if (defaultCompanyId) {
                companyStore.setCompanyId(defaultCompanyId);

                if (tenant?.company) {
                    companyStore.data = {
                        id: defaultCompanyId,
                        ...tenant.company,
                    };
                    companyStore.settings = {
                        bank_institutions: [],
                        bank_details: null,
                        ...(tenant.company.settings || {}),
                    };
                }

                // Secondary per-company data — must NOT abort the tenant switch.
                // The critical context (tenantId/slug/roles) is already set above;
                // if these tenant-scoped reads fail (e.g. the central / movepro
                // admin context, which 403s on tenant data), letting the error
                // propagate left the tenant-selection modal open and the UI hung.
                // Log and continue so the switch always completes.
                try {
                    const settings = await companyStore.retrieveSettings(defaultCompanyId);
                    if (settings?.stripe_id) {
                        await stripePaymentStore.initStripe(settings.stripe_id);
                    }
                } catch (error) {
                    console.error('setTenant: company settings/stripe load failed; continuing', error);
                }
            }

            // this.navigation = navigation.populateNavigation(this.isTenantContext, [...this.data.roles]);
            // Initial navigation build - will be updated by watcher
            this.rebuildNavigation(false); // Start with desktop view

            if (fetchNotifications && !this.isCentralContext) {
                try {
                    const notifications = useNotificationsStore();
                    await notifications.fetchUnreadCount();

                    const shouldFetchJobCounts = !this.isRestrictedFieldStaff();
                    const shouldFetchInboxCounts = !this.isOnlyFieldStaff();

                    if (shouldFetchJobCounts) {
                        const jobCounts = useJobCountsStore();
                        await jobCounts.fetchCounts();
                    }

                    if (shouldFetchInboxCounts) {
                        const inboxCounts = useInboxCountsStore();
                        await inboxCounts.fetchCounts();
                    }

                    if (window.echo) {
                        notifications.stopListener();

                        if (shouldFetchJobCounts) {
                            const jobCounts = useJobCountsStore();
                            jobCounts.stopListener();
                        }

                        if (shouldFetchInboxCounts) {
                            const inboxCounts = useInboxCountsStore();
                            inboxCounts.stopListener();
                        }

                        window.echo.leaveAllChannels();

                        notifications.startListener();

                        if (shouldFetchJobCounts) {
                            const jobCounts = useJobCountsStore();
                            jobCounts.startListener();
                        }

                        if (shouldFetchInboxCounts) {
                            const inboxCounts = useInboxCountsStore();
                            inboxCounts.startListener();
                        }

                        // leaveAllChannels() above also drops the App-level
                        // staff-assignment subscription, which nothing else owns.
                        resubscribeStaffAssignmentRealtime();
                    } else {
                        console.log('Echo not available for WebSocket setup');
                    }
                } catch (_e) {
                    console.warn('Notification/job count setup failed:', _e?.message);
                }
            }
        },

        async showTenantModal() {
            // Refresh tenants list without triggering tenant switch
            await this.refreshTenants();
            this.tenantModalStatus = true;
        },

        async refreshTenants() {
            // Fetch fresh user data but don't call setTenant
            // This is used when opening the tenant modal to get updated tenant list
            try {
                const { data } = await sanctum.me();
                this.data.tenants = data.tenants;
                this.data.pending_invites_count = data.pending_invites_count ?? 0;
            } catch (error) {
                console.error('Failed to refresh tenants:', error);
            }
        },

        hideTenantModal() {
            this.tenantModalStatus = false;
        },

        toggleTenantModal() {
            this.tenantModalStatus = this.tenantModalStatus ? false : true;
        },

        /**
         * Never throws. Building the sidebar is presentation, and `setTenant` calls it
         * outside a try/catch — an error here used to propagate up and abort the whole
         * login, leaving the user on /login with no explanation. A broken sidebar is
         * recoverable; a broken sign-in is not.
         */
        rebuildNavigation(isMobile) {
            if (this.data && this.data.roles) {
                const currentTenant = this.data?.tenants?.find((t) => t.id === parseInt(this.tenantId, 10));
                const features = { waybills: !!currentTenant?.waybills_enabled };

                try {
                    this.navigation = navigation.populateNavigation(
                        this.isTenantContext,
                        [...this.data.roles],
                        isMobile,
                        features,
                    );
                } catch (error) {
                    Sentry.captureException(error, {
                        tags: { nav_build_failure: 'populateNavigation' },
                        extra: { roles: [...this.data.roles], tenantId: this.tenantId },
                    });

                    this.navigation = [];
                }
            }
        },

        async resolvePostAuthRoute() {
            const tenants = this.data?.tenants || [];
            const pendingInvitesCount = this.data?.pending_invites_count || 0;

            // Previously selected tenant is still valid — go straight through
            if (this.tenantUserSelected && this.tenantId) {
                const isValid = tenants.some((t) => t.id === parseInt(this.tenantId));
                if (isValid) {
                    return { name: this.isRestrictedFieldStaff() ? 'Home' : 'Dashboard' };
                }
            }

            // Has admin tenant 1000 — auto-select it and go to dashboard
            if (tenants.some((t) => t.id === 1000)) {
                if (this.tenantId !== 1000) {
                    await this.setTenant(1000, true);
                }
                return { name: 'Dashboard' };
            }

            // Exactly 1 tenant and no pending invites — auto-select and go straight
            if (tenants.length === 1 && pendingInvitesCount === 0) {
                return { name: this.isRestrictedFieldStaff() ? 'Home' : 'Dashboard' };
            }

            // 0 or >1 total options (tenants + invites) without 1000 — tenant select screen
            return { name: 'SelectTenant' };
        },
    },
    getters: {
        companyHasStripeAccount: (state) => !!state.data?.company?.settings?.stripe_id,
        isAuthenticated: (state) => {
            return state.authenticated;
        },

        // Roles for the active tenant, or [] when not yet resolvable.
        currentRoles: (state) => getCurrentRoles(state),

        isCentralContext(state) {
            return state.data?.company_id === 1000 && state.tenantId === 1000 && state.tenantSlug === 'movepro';
        },

        isTenantContext() {
            return this.isCentralContext === false;
        },

        hasMultipleTenants(state) {
            return state.data?.tenants?.length > 1;
        },

        hasPendingInvites(state) {
            return (state.data?.pending_invites_count || 0) > 0;
        },

        isRemovalist(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('MANAGER');
        },

        isAdmin(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('ADMIN');
        },

        isOwner(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('OWNER');
        },

        canSeeFinancialInformation(state) {
            const roles = getCurrentRoles(state);
            if (
                roles.some((role) => ['SUPER', 'OWNER', 'ADMIN', 'MANAGER', 'FINANCE', 'OPS', 'SALES'].includes(role))
            ) {
                return true;
            }

            // Crew (field staff / contractors) see financial information only when the company has
            // opted in via the display_prices_to_crew setting. Any other non-privileged role never
            // sees it, even with the setting on. The API redacts money fields under the same flag.
            const isCrew = roles.some((role) => ['FIELD_STAFF', 'CONTRACTOR'].includes(role));

            return isCrew && !!useCompanyStore().settings?.display_prices_to_crew;
        },

        // Mirrors the backend `scheduler-view` gate (AuthServiceProvider) plus the
        // Gate::before platform grant — roles that can fetch diary/schedule data.
        canViewScheduler(state) {
            const roles = getCurrentRoles(state);
            return roles.some((role) =>
                [
                    'SUPER',
                    'MOVEPRO_ADMIN',
                    'OWNER',
                    'ADMIN',
                    'MANAGER',
                    'OPS',
                    'SALES',
                    'FIELD_STAFF',
                    'CONTRACTOR',
                ].includes(role),
            );
        },

        // The Gate::before platform grant (User::isMoveproPlatformUser): a Movepro-company user
        // holding a platform role on any tenant, not every Movepro-company user.
        isMoveproPlatformUser(state) {
            return (
                state.isMoveproSuperAdminUser &&
                (state.data?.tenants ?? []).some((tenant) =>
                    (tenant.roles ?? []).some((role) => ['SUPER', 'MOVEPRO_ADMIN', 'DEVELOPER'].includes(role)),
                )
            );
        },

        // Mirrors the backend `scheduler-crud` gate plus the Gate::before platform grant: who may
        // edit diary tasks and lock dates. Other roles the user holds never take this away.
        canSchedule(state) {
            if (state.isMoveproPlatformUser) {
                return true;
            }

            const roles = getCurrentRoles(state);
            return roles.some((role) => ['SUPER', 'OWNER', 'ADMIN', 'MANAGER', 'OPS'].includes(role));
        },

        // Mirrors the API's task-status rule: a user holding a crew role updates only tasks they're
        // on, unless they also hold a role `scheduler-update-status` admits on its own.
        canUpdateAnyTaskStatus(state) {
            if (state.isMoveproPlatformUser) {
                return true;
            }

            const roles = getCurrentRoles(state);
            const holdsCrewRole = roles.some((role) => ['FIELD_STAFF', 'CONTRACTOR'].includes(role));
            return (
                !holdsCrewRole ||
                roles.some((role) => ['SUPER', 'OWNER', 'ADMIN', 'MANAGER', 'OPS', 'SALES'].includes(role))
            );
        },

        // Mirrors User::seesOnlyCrewSharedFiles: only a crew-only user (every role Field Staff or
        // Contractor) is held to crew rules on order files (shared files only, no sharing toggle).
        // Any other role lifts them, whatever crew role sits beside it.
        seesOnlyCrewSharedFiles(state) {
            if (state.isMoveproPlatformUser) {
                return false;
            }

            const roles = getCurrentRoles(state);
            return roles.length > 0 && roles.every((role) => ['FIELD_STAFF', 'CONTRACTOR'].includes(role));
        },

        // Mirrors User::writesOnlyCrewNotes: a crew role without a role that may create an order note
        // on its own (Owner, Admin, Manager, Ops, Sales) writes only notes crew can see.
        writesOnlyCrewNotes(state) {
            if (state.isMoveproPlatformUser) {
                return false;
            }

            const roles = getCurrentRoles(state);
            return (
                roles.some((role) => ['FIELD_STAFF', 'CONTRACTOR'].includes(role)) &&
                !roles.some((role) => ['OWNER', 'ADMIN', 'MANAGER', 'OPS', 'SALES'].includes(role))
            );
        },

        // Mirrors the backend `manage-stripe-external-account` gate (AuthServiceProvider)
        // plus the Gate::before platform grant. It governs the Stripe Connect sessions
        // with `external_account_collection` on — the banner, account management,
        // onboarding and the full payouts session — which is to say who can change
        // the bank account the company's payouts land in.
        //
        // Presentation only. The server is the enforcement point, because every one of
        // those endpoints is reachable without the CRM (CU-86d431060). MANAGER and OPS
        // are absent deliberately: both manage payment settings, neither moves the money.
        canManageStripeExternalAccount(state) {
            const roles = getCurrentRoles(state);
            return roles.some((role) => ['SUPER', 'MOVEPRO_ADMIN', 'OWNER', 'ADMIN', 'FINANCE'].includes(role));
        },

        isFinance(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('FINANCE');
        },

        isAgent(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('SALES');
        },

        // The Inbox opens on the user's work (own items plus unassigned SMS and
        // calls), the set the badges count. Decided by the API per tenant; true
        // for every role since CU-14zcqntn6kr.
        inboxDefaultsToOwn() {
            return this.getTenant?.inbox_defaults_to_own === true;
        },

        isTriage(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('OPS');
        },

        isAdminOrSuper(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('ADMIN') || roles.includes('SUPER');
        },

        isOwnerOrSuper(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('OWNER') || roles.includes('SUPER');
        },

        isManager(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('MANAGER');
        },

        isSuper(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('SUPER');
        },

        isDev(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('DEVELOPER');
        },

        // only applies when you're in the Movepro tenant view (i.e. when the active tenant is Movepro)
        isMoveproSuperAdminView(state) {
            return state.isMoveproAdmin && state.hasAnyRole(['SUPER', 'MOVEPRO_ADMIN', 'DEVELOPER']);
        },

        // applies regardless of tenant context (i.e. when you're a Movepro user in any tenant)
        isMoveproSuperAdminUser(state) {
            return state.data?.company_id === 1000;
        },

        // we should migrate usage of this to isMoveproSuperAdminView and remove this getter
        // we also need to audit use of this and .isMoveproAdmin in the codebase as it's ambiguous
        // and potentially used incorrectly or with incorrect assumptions about when it's truthy
        isMoveproSuperAdmin(state) {
            return state.isMoveproSuperAdminView;
        },

        // true only for SUPER role users — used for platform-exclusive features (security settings, audit log)
        isSuperOnly(state) {
            return state.isMoveproAdmin && state.hasAnyRole(['SUPER']);
        },

        companyId(state) {
            return state.data?.company_id;
        },

        isOnboarded(state) {
            const roles = getCurrentRoles(state);
            return roles.includes('OWNER') || roles.includes('ADMIN');
        },

        getUser(state) {
            return state.data;
        },

        getUserId(state) {
            return state.data?.id;
        },

        getTenant(state) {
            if (state.tenantId) {
                return state.data?.tenants?.find((tenant) => tenant.id === parseInt(state.tenantId, 10));
            }

            return null;
        },

        getTimezone(state) {
            return state.data?.timezone;
        },

        getAvatar(state) {
            return state.data?.avatar;
        },

        getFullName(state) {
            if (state.data && state.data.fullname) {
                return state.data.fullname;
            }
        },

        getHourlyRate(state) {
            return state.data?.hourly_rate ?? 0;
        },

        getInitials(state) {
            if (state.data) {
                const firstName = state.data.first_name || '';
                const lastName = state.data.last_name || '';

                const firstInitial = firstName.charAt(0).toUpperCase();
                const lastInitial = lastName.charAt(0).toUpperCase();

                return `${firstInitial}${lastInitial}`;
            }
            return '';
        },

        getFirstName(state) {
            if (state.data && state.data.first_name) {
                return state.data.first_name;
            }
        },

        getLastName(state) {
            if (state.data && state.data.last_name) {
                return state.data.last_name;
            }
        },

        getUsername(state) {
            if (state.data) {
                return state.data.username;
            }
        },

        getRoles(state) {
            return getCurrentRoles(state);
        },

        getCompanyId(state) {
            if (state.data) {
                return state.data.company_id;
            }
        },

        getTeam(state) {
            return state.data?.team;
        },

        // gates
        hasRole: (state) => {
            return (role) => {
                const roles = getCurrentRoles(state);
                return roles.includes(role);
            };
        },

        unlessRole: (state) => {
            return (role) => {
                const roles = getCurrentRoles(state);
                return !roles.includes(role);
            };
        },

        hasAnyRole: (state) => {
            return (roleList, alwaysAllowOwnerAndAdmin = true) => {
                const roles = getCurrentRoles(state);
                if (roles.length === 0) {
                    return false;
                }

                if (alwaysAllowOwnerAndAdmin && (roles.includes('OWNER') || roles.includes('ADMIN'))) {
                    return true;
                }

                const rolesToCheck = Array.isArray(roleList) ? roleList : [roleList];
                return rolesToCheck.some((r) => roles.includes(r));
            };
        },

        hasEveryRole: (state) => {
            return (rolesToCheck) => {
                const roles = getCurrentRoles(state);
                if (roles.length === 0) {
                    return false;
                }

                // Admin roles have access to everything
                if (roles.includes('OWNER') || roles.includes('ADMIN')) {
                    return true;
                }
                return rolesToCheck.every((role) => roles.includes(role));
            };
        },

        // Crew restrictions apply only when every tenant role is a crew role; any other role lifts them.
        // Kept as an alias of isOnlyFieldStaff so its ~60 call sites need no edit.
        isRestrictedFieldStaff() {
            return () => this.isOnlyFieldStaff();
        },

        // Helper method to check if user has only FIELD_STAFF or CONTRACTOR role
        isOnlyFieldStaff: (state) => {
            return () => {
                const roles = getCurrentRoles(state);
                if (roles.length === 0) {
                    return false;
                }

                const fieldRoles = roles.filter((r) => r === 'FIELD_STAFF' || r === 'CONTRACTOR');
                return fieldRoles.length > 0 && fieldRoles.length === roles.length;
            };
        },

        // Helper method to check if user is a contractor (for timesheet restrictions)
        isContractor: (state) => {
            return () => {
                const roles = getCurrentRoles(state);
                return roles.includes('CONTRACTOR');
            };
        },

        // Helper method to check if user should see count badges in navigation
        shouldShowCountBadges: (state) => {
            return () => {
                const roles = getCurrentRoles(state);
                if (roles.length === 0) {
                    return false;
                }

                // Show counts only for roles that have access to orders/leads
                return roles.some((role) =>
                    ['SUPER', 'ADMIN', 'OWNER', 'MANAGER', 'FINANCE', 'SALES', 'OPS'].includes(role),
                );
            };
        },

        getBaseAccent: (state) => {
            if (state.tenantId) {
                const tenant = state.data?.tenants?.find((t) => t.id === parseInt(state.tenantId, 10));
                if (tenant?.is_branded_crm_enabled && tenant?.accent) {
                    return tenant.accent;
                }
            }

            return '#1F3DF0';
        },

        getTenantAccent: (state) => {
            if (state.tenantId) {
                return state.data?.tenants?.find((tenant) => tenant.id === parseInt(state.tenantId, 10))?.accent;
            }

            return '#1F3DF0';
        },

        getTenantLogo: (state) => {
            if (state.tenantId) {
                const tenant = state.data?.tenants?.find((t) => t.id === parseInt(state.tenantId, 10));
                if (tenant?.is_branded_crm_enabled && tenant?.logo) {
                    return tenant.logo;
                }
            }

            return null;
        },

        hasAuth() {
            return (rolesToCheck) => {
                return this.hasAnyRole(rolesToCheck, true);
            };
        },
    },
});
